/**
 * API Services Module
 * Responsible for all API calls and data fetching
 */

import axios from 'axios';
import { getApiConfig } from './configServices';

class ApiServices {
  constructor() {
    const config = getApiConfig();
    // Use config baseURL which will be determined by environment
    this.baseURL = config.baseURL;
    this.timeout = config.timeout;
    
    // Create axios instance with default config
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: this.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor for logging/auth
    this.client.interceptors.request.use(
      (config) => {
       
        return config;
      },
      (error) => {
        console.error('API Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        console.error('API Response Error:', error);
        return Promise.reject(error);
      }
    );
  }



  /**
   * Fetch list of counties
   * @returns {Promise<Object>} Object containing counties array
   */
  async fetchCounties() {
    try {
      const response = await this.client.get('/populate-counties');
      return response.data;
    } catch (error) {
      console.error('Error fetching counties:', error);
      throw error;
    }
  }

  /**
   * Fetch list of regions
   * @returns {Promise<Object>} Object containing regions array
   */
  async fetchRegions() {
    try {
      const response = await this.client.get('/populate-regions');
      return response.data;
    } catch (error) {
      console.error('Error fetching regions:', error);
      throw error;
    }
  }

  /**
   * Fetch stations for a specific county
   * @param {string} county - County name
   * @returns {Promise<Object>} Object containing stations array for the county
   */
  async fetchStationsForCounty(county) {
    try {
      const response = await this.client.get('/populate-stations', {
        params: { county }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching stations for county ${county}:`, error);
      throw error;
    }
  }

  /**
   * Fetch stations for a specific region
   * @param {string} region - Region name
   * @returns {Promise<Object>} Object containing stations array for the region
   */
  async fetchStationsForRegion(region) {
    try {
      const response = await this.client.get('/populate-stations', {
        params: { region }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching stations for region ${region}:`, error);
      throw error;
    }
  }


  /**
   * Update station data
   * @param {string} stationId - Station ID to update
   * @param {Object} updates - Object containing field updates
   * @returns {Promise<Object>} Update response
   */
  async updateStationData(stationId, updates) {
    try {
      const response = await this.client.post('/update-data', {
        site: stationId,
        updates: updates,
      });
      return response.data;
    } catch (error) {
      console.error('Error updating station data:', error);
      throw error;
    }
  }

  /**
   * Download station data in specified format
   * @param {Object} params - Download parameters
   * @param {string} params.format - Format type (csv, excel, json)
   * @param {string} params.site - Optional site filter
   * @param {string} params.region - Optional region filter
   * @param {number} params.limit - Optional limit
   * @returns {Promise<Object>} Download response with base64 content
   */
  async downloadData(params = {}) {
    try {
      const response = await this.client.get('/download-data', { params });
      return response.data;
    } catch (error) {
      console.error('Error downloading data:', error);
      throw error;
    }
  }


  /**
   * Fetch detailed station info including geometry data
   * @param {string} masterid - Master ID for the station
   * @returns {Promise<Object>} Station info with geometry data
   */
  async fetchStationInfo(masterid) {
    try {
      const response = await this.client.get('/get-station-info', {
        params: { masterid }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching station info for masterid ${masterid}:`, error);
      throw error;
    }
  }

  /**
   * Download all data from vw_refscreens_final
   * Writes CSV to S3 and returns presigned download URL
   * @returns {Promise<Object>} Download URL and metadata
   */
  async downloadAllData() {
    try {
      const response = await this.client.get('/download-all-data');
      return response.data;
    } catch (error) {
      console.error('Error downloading all data:', error);
      throw error;
    }
  }

  /**
   * Submit reference status feedback
   * @param {Object} feedbackData - Feedback data object
   * @param {string} feedbackData.masterid - Master ID of the station
   * @param {string} feedbackData.email - User email
   * @param {string} feedbackData.refstatus_final - Current reference status
   * @param {string} feedbackData.refstatus_feedback - User feedback text
   * @param {string} feedbackData.refstatus_photos - 'yes' or 'no'
   * @returns {Promise<Object>} Submission response
   */
  async submitRefstatusFeedback(feedbackData) {
    try {
      const response = await this.client.post('/submit-refstatus-feedback', feedbackData);
      return response.data;
    } catch (error) {
      console.error('Error submitting reference status feedback:', error);
      throw error;
    }
  }

  /**
   * Submit flow status feedback
   * @param {Object} feedbackData - Feedback data object
   * @param {string} feedbackData.masterid - Master ID of the station
   * @param {string} feedbackData.email - User email
   * @param {string} feedbackData.flowstatus_final - Current flow status
   * @param {string} feedbackData.flowstatus_updated - User's suggested flow status
   * @param {string} feedbackData.flowstatus_feedback - User feedback text
   * @param {string} feedbackData.flowstatus_photos - 'yes' or 'no'
   * @returns {Promise<Object>} Submission response
   */
  async submitFlowstatusFeedback(feedbackData) {
    try {
      const response = await this.client.post('/submit-flowstatus-feedback', feedbackData);
      return response.data;
    } catch (error) {
      console.error('Error submitting flow status feedback:', error);
      throw error;
    }
  }

  /**
   * Submit delineation status feedback
   * @param {Object} feedbackData - Feedback data object
   * @param {string} feedbackData.masterid - Master ID of the station
   * @param {string} feedbackData.email - User email
   * @param {string} feedbackData.delineation_correct - 'yes' or 'no'
   * @returns {Promise<Object>} Submission response
   */
  async submitDelineationFeedback(feedbackData) {
    try {
      const response = await this.client.post('/submit-delineation-feedback', feedbackData);
      return response.data;
    } catch (error) {
      console.error('Error submitting delineation feedback:', error);
      throw error;
    }
  }

  /**
   * Upload a photo directly to S3 using presigned URL
   * @param {File} file - The file object to upload
   * @param {string} masterid - Master ID of the station
   * @param {string} feedbackType - 'refstatus' or 'flowstatus'
   * @returns {Promise<string>} The final S3 URL of the uploaded file
   */
  async uploadPhoto(file, masterid, feedbackType = 'refstatus') {
    try {
      // Step 1: Get presigned URL from backend
      const presignedResponse = await this.client.post('/upload-photo', {
        file_name: file.name,
        file_type: file.type,
        masterid: masterid,
        feedback_type: feedbackType
      });
      
      const { presigned_url, url } = presignedResponse.data;
      
      if (!presigned_url) {
        throw new Error('Failed to get presigned URL');
      }
      
      // Step 2: Upload directly to S3 using presigned URL
      await axios.put(presigned_url, file, {
        headers: {
          'Content-Type': file.type
        }
      });
      
      console.log('File uploaded successfully to:', url);
      return url;
    } catch (error) {
      console.error('Error uploading photo:', error);
      throw error;
    }
  }

  /**
   * Zip multiple files and upload to S3 using presigned URL
   * @param {File[]} files - Array of file objects to zip and upload
   * @param {string} masterid - Master ID of the station
   * @param {string} feedbackType - 'refstatus' or 'flowstatus'
   * @returns {Promise<string>} The final S3 URL of the uploaded zip file
   */
  async uploadFilesAsZip(files, masterid, feedbackType = 'refstatus') {
    // Allowed file types (images, Excel, CSV only)
    const ALLOWED_TYPES = [
      'image/jpeg', 'image/png',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ];
    const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'xls', 'xlsx', 'csv'];
    const MAX_TOTAL_SIZE = 50 * 1024 * 1024; // 50MB
    const MAX_FILES = 20;

    try {
      // Validate number of files
      if (files.length > MAX_FILES) {
        alert(`Too many files selected: ${files.length}\n\nMaximum ${MAX_FILES} files allowed.`);
        throw new Error(`Too many files. Maximum ${MAX_FILES} files allowed.`);
      }

      // Validate total file size
      const totalSize = files.reduce((sum, f) => sum + f.size, 0);
      if (totalSize > MAX_TOTAL_SIZE) {
        alert(`Total file size too large: ${(totalSize / 1024 / 1024).toFixed(1)}MB\n\nMaximum allowed: 50MB`);
        throw new Error(`Total file size (${(totalSize / 1024 / 1024).toFixed(1)}MB) exceeds 50MB limit`);
      }

      // Validate each file
      for (const file of files) {
        // Check file type
        if (!ALLOWED_TYPES.includes(file.type)) {
          const ext = file.name.split('.').pop()?.toLowerCase();
          if (!ALLOWED_EXTENSIONS.includes(ext)) {
            alert(`File type not allowed: ${file.name}\n\nAllowed file types: JPG, PNG, Excel (XLS/XLSX), CSV`);
            throw new Error(`File type not allowed: ${file.name}. Allowed: images, Excel, CSV`);
          }
        }
        
        // Check for suspicious file names
        if (/[<>:"\/\\|?*\x00-\x1f]/.test(file.name)) {
          alert(`Invalid characters in file name: ${file.name}\n\nPlease rename your file and try again.`);
          throw new Error(`Invalid characters in file name: ${file.name}`);
        }
        
        // Check for double extensions (e.g., file.jpg.exe)
        const parts = file.name.split('.');
        if (parts.length > 2) {
          const suspiciousExts = ['exe', 'bat', 'cmd', 'sh', 'ps1', 'vbs', 'js', 'msi', 'dll'];
          if (parts.some(p => suspiciousExts.includes(p.toLowerCase()))) {
            alert(`Suspicious file detected: ${file.name}\n\nThis file type is not allowed.`);
            throw new Error(`Suspicious file name detected: ${file.name}`);
          }
        }
      }

      // Dynamically import JSZip
      const JSZip = (await import('jszip')).default;
      
      // Create zip file with sanitized names
      const zip = new JSZip();
      for (const file of files) {
        // Sanitize filename: remove special chars, limit length
        const safeName = file.name
          .replace(/[^a-zA-Z0-9._-]/g, '_')
          .substring(0, 100);
        zip.file(safeName, file);
      }
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      // Step 1: Get presigned URL from backend for zip file
      const presignedResponse = await this.client.post('/upload-photo', {
        file_name: 'photos.zip',
        file_type: 'application/zip',
        masterid: masterid,
        feedback_type: feedbackType
      });
      
      const { presigned_url, url } = presignedResponse.data;
      
      if (!presigned_url) {
        throw new Error('Failed to get presigned URL');
      }
      
      // Step 2: Upload zip directly to S3 using presigned URL
      await axios.put(presigned_url, zipBlob, {
        headers: {
          'Content-Type': 'application/zip'
        }
      });
      
      console.log('Zip file uploaded successfully to:', url);
      return url;
    } catch (error) {
      console.error('Error uploading zip:', error);
      throw error;
    }
  }

}

// Export singleton instance
const apiServices = new ApiServices();
export default apiServices;

// Export bound individual methods for convenience
export const fetchCounties = apiServices.fetchCounties.bind(apiServices);
export const fetchRegions = apiServices.fetchRegions.bind(apiServices);
export const fetchStationsForCounty = apiServices.fetchStationsForCounty.bind(apiServices);
export const fetchStationsForRegion = apiServices.fetchStationsForRegion.bind(apiServices);
export const updateStationData = apiServices.updateStationData.bind(apiServices);
export const downloadData = apiServices.downloadData.bind(apiServices);
export const downloadAllData = apiServices.downloadAllData.bind(apiServices);
export const fetchStationInfo = apiServices.fetchStationInfo.bind(apiServices);
export const submitRefstatusFeedback = apiServices.submitRefstatusFeedback.bind(apiServices);
export const submitFlowstatusFeedback = apiServices.submitFlowstatusFeedback.bind(apiServices);
export const uploadPhoto = apiServices.uploadPhoto.bind(apiServices);
export const uploadFilesAsZip = apiServices.uploadFilesAsZip.bind(apiServices);